
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface CompanySummary {
  id: string;
  taxId: string | null;
  businessName: string | null;
  companyType: string | null;
  enabled: boolean;
  isPaused?: boolean;
  deedReviewStatus: string | null;
  powerOfAttorneyReviewStatus: string | null;
  identityDocumentReviewStatus: string | null;
  hasSignedPromissoryNote: boolean;
  signerCount: number;
  acquirers: { slug: string; integrationStatus: string; activated: boolean; hasMandateDocuments: boolean }[];
  createdAt: string;
}

interface PendingRegistrationItem {
  id: string;
  phone: string;
  createdAt: string;
  expiresAt: string;
  processedAt: string | null;
}

interface PendingRegistrationsListResult {
  page: number;
  pageSize: number;
  total: number;
  items: PendingRegistrationItem[];
}

function ReviewBadge({ status }: { status: string | null }) {
  if (!status) return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">Sin subir</span>;
  if (status === 'approved') return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Aprobado</span>;
  if (status === 'pending') return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Pendiente</span>;
  if (status === 'rejected') return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />Rechazado</span>;
  return <Badge variant="outline" className="text-[11px]">{status}</Badge>;
}

function SkeletonRows() {
  return Array.from({ length: 5 }).map((_, i) => (
    <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
      {Array.from({ length: 7 }).map((_, j) => (
        <td key={j} className="px-4 py-3.5">
          <div className="h-3.5 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" style={{ width: `${50 + Math.random() * 40}%` }} />
        </td>
      ))}
    </tr>
  ));
}

export default function EmpresasPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [pendingItems, setPendingItems] = useState<PendingRegistrationItem[]>([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    loadCompanies();
    loadPending();
  }, []);

  async function loadCompanies() {
    try {
      const url = getAdminEndpointUrl('companies', 'list');
      const data = await adminHttpClient.get<CompanySummary[]>(url);
      setCompanies(data);
    } catch (err) {
      console.error('Error cargando empresas:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadPending() {
    try {
      const url = getAdminEndpointUrl('pendingRegistrations', 'list');
      const data = await adminHttpClient.get<PendingRegistrationsListResult>(url);
      setPendingItems(data.items);
      setPendingTotal(data.total);
    } catch (err) {
      console.error('Error cargando registros pendientes:', err);
    }
  }

  async function handleMarkProcessed(id: string) {
    setProcessingId(id);
    try {
      await adminHttpClient.post(getAdminEndpointUrl('pendingRegistrations', 'markProcessed', { id }), {});
      await loadPending();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    } finally {
      setProcessingId(null);
    }
  }

  const filtered = companies.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.taxId?.toLowerCase().includes(q)) || (c.businessName?.toLowerCase().includes(q));
  });

  const pendingLabel = pendingTotal === 1 ? '1 comercio entregó su teléfono pero no se inscribió' : `${pendingTotal} comercios entregaron su teléfono pero no se inscribieron`;

  return (
    <AdminLayout title="Empresas" subtitle={`${companies.length} comercios registrados`}>
      <div className="space-y-5">
        {/* Alerta de pre-registros pendientes */}
        {pendingTotal > 0 && (
          <button
            type="button"
            onClick={() => setPendingOpen(true)}
            className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
          >
            <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-[20px] shrink-0">phone_missed</span>
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300 flex-1">{pendingLabel}</span>
            <span className="text-xs text-amber-600 dark:text-amber-400 shrink-0">Ver lista →</span>
          </button>
        )}

        {/* Search */}
        <div className="relative max-w-sm">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="material-symbols-outlined text-slate-400 text-[18px]">search</span>
          </span>
          <Input
            placeholder="Buscar por RUT o razon social..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 h-10"
          />
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">RUT</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Razon Social</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Tipo</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Documentos</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Pagare</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Adquirentes</th>
                <th className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Registro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <SkeletonRows />
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16">
                    <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-600">search_off</span>
                    <p className="text-sm text-slate-500 mt-2">No se encontraron empresas</p>
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/empresas/detalle?id=${c.id}`)}
                  >
                    <td className="px-4 py-3.5 text-sm font-mono text-slate-700 dark:text-slate-300">{c.taxId || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{c.businessName || <span className="text-slate-400 font-normal">Sin nombre</span>}</span>
                        {c.isPaused && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full">
                            <span className="material-symbols-outlined text-[12px]">pause_circle</span>
                            Pausado
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      {c.companyType === 'persona_natural' ? (
                        <span className="text-[11px] font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 px-2 py-0.5 rounded-full">Persona Natural</span>
                      ) : c.companyType === 'empresa' ? (
                        <span className="text-[11px] font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 px-2 py-0.5 rounded-full">Empresa</span>
                      ) : (
                        <span className="text-[11px] font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded-full">Sin definir</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5 flex-wrap">
                        {c.companyType === 'persona_natural' ? (
                          <ReviewBadge status={c.identityDocumentReviewStatus} />
                        ) : c.companyType === 'empresa' ? (
                          <>
                            <ReviewBadge status={c.deedReviewStatus} />
                            <ReviewBadge status={c.powerOfAttorneyReviewStatus} />
                          </>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      {c.hasSignedPromissoryNote ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                          <span className="material-symbols-outlined text-[12px]">check_circle</span>Si
                        </span>
                      ) : (
                        <span className="text-[11px] font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5 flex-wrap">
                        {c.acquirers.length === 0 ? (
                          <span className="text-sm text-slate-400">—</span>
                        ) : (
                          c.acquirers.map((a) => (
                            <span
                              key={a.slug}
                              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                                a.activated
                                  ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400'
                                  : 'text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400'
                              }`}
                            >
                              {a.slug}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-500 dark:text-slate-400">
                      {new Date(c.createdAt).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: teléfonos pre-registrados pendientes */}
      <Dialog open={pendingOpen} onOpenChange={setPendingOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="material-symbols-outlined text-amber-600 text-[20px]">phone_missed</span>
              Teléfonos sin inscripción
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {pendingTotal === 0
                ? 'No hay prospectos pendientes.'
                : `${pendingTotal} ${pendingTotal === 1 ? 'prospecto entregó su número' : 'prospectos entregaron su número'} pero no completaron el registro. Márcalos como procesados una vez que los contactes.`}
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {pendingItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {pendingTotal === 0 ? '¡Todo en orden! No hay pendientes.' : 'Cargando…'}
              </p>
            ) : (
              <div className="divide-y divide-border">
                {pendingItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-medium text-foreground">{item.phone}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Dejó su número{' '}
                        {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true, locale: es })}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={processingId === item.id}
                      onClick={() => handleMarkProcessed(item.id)}
                      className="shrink-0"
                    >
                      {processingId === item.id ? 'Procesando…' : 'Marcar procesado'}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
